/**
 * @typedef {Object} DTCOrderAttribute
 * @property {string} name
 * @property {string} value
 */

/**
 * @typedef {Object} DTCCookieNames
 * @property {string} DTC
 * @property {string} LOCATION_ID
 * @property {string} PROGRAM_ID
 * @property {string} PRIORITY_OVERRIDE
 * @property {string} SOURCE
 */

/**
 * @typedef {Object} DTCData
 * @property {string|null} dtc
 * @property {string|null} location_id
 * @property {string|null} program_id
 * @property {string|null} priority_override
 * @property {string|null} source
 */

/**
 * DTC Cookie Manager
 * Manages cookies for tracking DTC (Direct to Consumer) marketing attribution
 *
 * Tracks:
 * - location_id: Provider location ID
 * - program_id: Marketing program ID
 * - dtc: Boolean flag indicating DTC attribution
 * - priority_override: Optional priority override value
 */
class DTCCookieManager {
  /**
   * @type {DTCCookieNames}
   */
  cookieNames;
  /**
   * @type {number}
   */
  defaultExpiry;
  constructor() {
    this.cookieNames = {
      DTC: "dtc",
      LOCATION_ID: "location_id",
      PROGRAM_ID: "program_id",
      PRIORITY_OVERRIDE: "priority_override",
      SOURCE: "source",
    };
    this.defaultExpiry = 365; // days (1 year)

    const isHealthcarePractitioner =
      document.body?.dataset.isHealthcarePractitioner === "true";
    const requireProviderBeforeCheckout =
      document.body?.dataset.requireProviderBeforeCheckout === "true";

    if (
      requireProviderBeforeCheckout &&
      !isHealthcarePractitioner &&
      this.hasCheckoutTargets()
    ) {
      this.bindCartCheckoutRequirement();
    }

    if (window.location.pathname === "/cart") {
      if (isHealthcarePractitioner) {
        this.updateCartAttributes();
        return;
      }

      if (
        !this.getCookie(this.cookieNames.LOCATION_ID) &&
        !this.getCookie(this.cookieNames.PROGRAM_ID)
      ) {
        console.warn(
          "DTC Cookie Manager: No location_id or program_id found on cart page. Initializing from URL parameters.",
        );
        this.replaceCheckoutButton();
      } else {
        this.updateCartAttributes();
      }
    }
  }

  /**
   * Update cart attributes when DTC cookies change
   * @returns {void}
   */
  updateCartAttributes() {
    const updateAttributes = () => {
      const body = JSON.stringify({
        attributes: {
          dtc: this.getCookie(this.cookieNames.DTC),
          location_id: this.getCookie(this.cookieNames.LOCATION_ID),
          program_id: this.getCookie(this.cookieNames.PROGRAM_ID),
          priority_override: this.getCookie(this.cookieNames.PRIORITY_OVERRIDE),
          source: this.getCookie(this.cookieNames.SOURCE),
        },
      });
      // @ts-ignore
      this.cartAttributesSyncPromise = fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        ...{ body },
      }).then((response) => {
        if (!response.ok) {
          console.error("Failed to update cart attributes:", response);
        }
        return response;
      });

      return this.cartAttributesSyncPromise;
    };

    const syncPromise = updateAttributes();

    if (this.cartAttributesBound) return syncPromise;

    this.cartAttributesBound = true;
    window.addEventListener("dtcCookieSet", updateAttributes);

    return syncPromise;
  }

  /**
   * Set a cookie with optional expiry
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} [days=this.defaultExpiry] - Expiry in days
   * @returns {void}
   */
  setCookie(name, value, days = this.defaultExpiry) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;

    window.dispatchEvent(
      new CustomEvent("dtcCookieSet", {
        detail: { name, value },
      }),
    );
  }

  /**
   * Get a cookie value
   * @param {string} name - Cookie name
   * @returns {string|null}
   */
  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  /**
   * Initialize from URL parameters (call on any page that might have marketing params)
   * @returns {void}
   */
  initializeFromUrlParams() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      let hasMarketingParams = false;

      // Priority protection: if user already has a priority-1 attribution (QR scan
      // or direct provider selection), don't let a lower-priority link overwrite it.
      const existingPriority = this.getCookie(this.cookieNames.PRIORITY_OVERRIDE);
      const existingProgramId = this.getCookie(this.cookieNames.PROGRAM_ID);
      const incomingPriority = urlParams.get("priority_override");
      const isProtected =
        existingPriority === "1" &&
        existingProgramId &&
        incomingPriority !== "1";

      // Check for location_id parameter
      const locationId = urlParams.get("location_id");
      if (locationId) {
        this.setLocationId(locationId);
        hasMarketingParams = true;
      }

      // Check for program_id parameter — protected if existing attribution is priority 1
      const programId = urlParams.get("program_id");
      if (programId) {
        if (isProtected) {
          console.log(
            "DTC: Priority protection — keeping existing program_id (" +
              existingProgramId +
              ") over incoming (" +
              programId +
              ")",
          );
        } else {
          this.setProgramId(programId);
        }
        hasMarketingParams = true;
      }

      // Check for priority_override parameter
      if (incomingPriority) {
        this.setPriorityOverride(incomingPriority);
        hasMarketingParams = true;
      }

      // Set DTC flag when any marketing parameters are present
      if (hasMarketingParams) {
        this.setDTC(true);
      }

      // Log current state for debugging
      if (hasMarketingParams) {
        const currentData = this.getAllDTCData();
      }
    } catch (error) {
      console.error("DTC: Error initializing URL parameters:", error);
    }
  }

  /**
   * Set DTC flag
   * @param {boolean} [value=true]
   * @returns {void}
   */
  setDTC(value = true) {
    this.setCookie(this.cookieNames.DTC, value.toString());
  }

  /**
   * Set location ID
   * @param {string} locationId
   * @returns {void}
   */
  setLocationId(locationId) {
    this.setCookie(this.cookieNames.LOCATION_ID, locationId);
  }

  /**
   * Set program ID
   * @param {string} programId
   * @returns {void}
   */
  setProgramId(programId) {
    this.setCookie(this.cookieNames.PROGRAM_ID, programId);
  }

  /**
   * Set priority override
   * @param {string} priority
   * @returns {void}
   */
  setPriorityOverride(priority) {
    this.setCookie(this.cookieNames.PRIORITY_OVERRIDE, priority);
  }

  /**
   * Persist a selected provider and emit the shared provider selection event.
   * @param {Record<string, any>} provider
   * @param {{priorityOverride?: string, dtc?: boolean, source?: string}} [options]
   * @returns {void}
   */
  assignProviderSelection(provider, options = {}) {
    if (!provider?.id) {
      console.error("DTC Cookie Manager: Cannot assign provider without an id.");
      return;
    }

    const {
      priorityOverride = "1",
      dtc = true,
      source = "manual",
    } = options;

    try {
      localStorage.setItem("provider_data", JSON.stringify(provider));
    } catch (error) {
      console.error("DTC Cookie Manager: Failed to persist provider data.", error);
    }

    this.setLocationId(provider.id);
    this.setPriorityOverride(priorityOverride);
    this.setDTC(dtc);

    window.dispatchEvent(
      new CustomEvent("providerSelected", {
        detail: {
          provider,
          providerId: provider.id,
          source,
        },
      }),
    );
  }

  /**
   * Set attribution source (e.g., "microsite", "qr", "link")
   * @param {string} source
   * @returns {void}
   */
  setSource(source) {
    this.setCookie(this.cookieNames.SOURCE, source);
  }

  /**
   * Get all DTC-related cookies as an object
   * @returns {DTCData}
   */
  getAllDTCData() {
    return {
      dtc: this.getCookie(this.cookieNames.DTC),
      location_id: this.getCookie(this.cookieNames.LOCATION_ID),
      program_id: this.getCookie(this.cookieNames.PROGRAM_ID),
      priority_override: this.getCookie(this.cookieNames.PRIORITY_OVERRIDE),
      source: this.getCookie(this.cookieNames.SOURCE),
    };
  }

  /**
   * Check if this is a DTC customer
   * @returns {boolean}
   */
  isDTCCustomer() {
    return this.getCookie(this.cookieNames.DTC) === "true";
  }

  /**
   * Clear all DTC cookies (useful for testing)
   * @returns {void}
   */
  clearAllDTCCookies() {
    Object.values(this.cookieNames).forEach((cookieName) => {
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    });
  }

  /**
   * Ensure DTC flag is set (call on cart page)
   * @returns {DTCData}
   */
  ensureDTCFlagOnCart() {
    const existingData = this.getAllDTCData();

    // If we have any tracking data, ensure DTC flag is set
    if (existingData.location_id || existingData.program_id) {
      this.setDTC(true);
    }

    return existingData;
  }

  /**
   * Get formatted data for Shopify order attributes
   * @returns {DTCOrderAttribute[]}
   */
  getOrderAttributes() {
    const data = this.getAllDTCData();
    const attributes = [];

    if (data.dtc) {
      attributes.push({ name: "dtc", value: data.dtc });
    }
    if (data.location_id) {
      attributes.push({ name: "location_id", value: data.location_id });
    }
    if (data.program_id) {
      attributes.push({ name: "program_id", value: data.program_id });
    }
    if (data.priority_override) {
      attributes.push({
        name: "priority_override",
        value: data.priority_override,
      });
    }
    if (data.source) {
      attributes.push({ name: "source", value: data.source });
    }

    return attributes;
  }

  /**
   * Replace checkout button with provider selection
   * @returns {void}
   */
  replaceCheckoutButton() {
    const selectProviderCheckoutButton = document.querySelector(
      ".select-provider-checkout-replace",
    );
    if (selectProviderCheckoutButton) {
      selectProviderCheckoutButton.classList.add(
        "checkout-provider-btn-visible",
      );
      selectProviderCheckoutButton.addEventListener("click", (event) => {
        event.preventDefault();
        const providersMapSection = document.querySelector(
          ".providers-map-section",
        );
        if (providersMapSection) {
          providersMapSection.scrollIntoView({ behavior: "smooth" });
        } else {
          window.location.href = "/pages/find-providers";
        }
      });
    }
  }

  /**
   * Keep cart checkout actions in sync with provider selection.
   * Provider data is synced before checkout when present, but customers without
   * a selected provider can continue so they can be matched after checkout.
   * @returns {void}
   */
  bindCartCheckoutRequirement() {
    if (this.cartCheckoutRequirementBound) {
      this.updateCartCheckoutState();
      return;
    }

    this.cartCheckoutRequirementBound = true;
    this.updateCartAttributes();
    this.updateCartCheckoutState();

    window.addEventListener("dtcCookieSet", (event) => {
      // @ts-ignore
      if (event.detail?.name === this.cookieNames.LOCATION_ID) {
        this.updateCartCheckoutState();
      }
    });

    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        this.updateCartCheckoutState();
      }
    });

    window.addEventListener("providerSelected", () => {
      this.updateCartCheckoutState();
    });

    this.observeCartCheckoutTargets();

    document.addEventListener(
      "submit",
      (event) => {
        if (!this.isCheckoutFormSubmit(event)) return;

        if (this.checkoutSubmitAllowed) {
          this.checkoutSubmitAllowed = false;
          return;
        }

        if (!this.isProviderRequirementEnabled()) return;

        event.preventDefault();
        event.stopPropagation();

        if (this.isProviderRequiredForCheckout()) {
          this.showProviderRequiredMessage(event.target);
          return;
        }

        this.syncCartAttributesAndSubmit(event.target, event.submitter);
      },
      true,
    );

    document.addEventListener(
      "click",
      (event) => {
        const checkoutButton = event.target?.closest?.(
          "[data-provider-required-checkout]",
        );
        if (!checkoutButton || !this.isProviderRequiredForCheckout()) return;

        event.preventDefault();
        event.stopPropagation();
        this.showProviderRequiredMessage(checkoutButton);
      },
      true,
    );
  }

  hasCheckoutTargets() {
    return !!document.querySelector(
      "#cart, #CartDrawer-Form, [data-provider-required-checkout]",
    );
  }

  observeCartCheckoutTargets() {
    const targets = [
      document.getElementById("CartDrawer"),
      document.getElementById("main-cart-footer"),
    ].filter(Boolean);

    targets.forEach((target) => {
      const observer = new MutationObserver(() => {
        this.updateCartCheckoutState();
      });

      observer.observe(target, { childList: true, subtree: true });
    });
  }

  hasSelectedProvider() {
    return !!this.getCookie(this.cookieNames.LOCATION_ID);
  }

  isProviderRequirementEnabled() {
    const isHealthcarePractitioner =
      document.body?.dataset.isHealthcarePractitioner === "true";
    const requireProviderBeforeCheckout =
      document.body?.dataset.requireProviderBeforeCheckout === "true";

    return requireProviderBeforeCheckout && !isHealthcarePractitioner;
  }

  allowsProviderMatchAfterCheckout() {
    return !!document.querySelector(
      '[data-provider-match-notice], [data-cart-drawer-provider][data-hide-empty-provider-prompt="true"]',
    );
  }

  isProviderRequiredForCheckout() {
    return (
      this.isProviderRequirementEnabled() &&
      !this.allowsProviderMatchAfterCheckout() &&
      !this.hasSelectedProvider()
    );
  }

  isCheckoutFormSubmit(event) {
    const submitter = event.submitter;
    if (submitter?.matches?.("[data-provider-required-checkout]")) {
      return true;
    }

    const form = event.target;
    return (
      form?.matches?.("#cart, #CartDrawer-Form") &&
      form.querySelector?.("[data-provider-required-checkout]")
    );
  }

  async syncCartAttributesAndSubmit(form, submitter) {
    if (!form) return;

    submitter?.setAttribute?.("aria-busy", "true");

    try {
      await this.updateCartAttributes();
      this.checkoutSubmitAllowed = true;

      if (typeof form.requestSubmit === "function") {
        form.requestSubmit(submitter || undefined);
      } else {
        form.submit();
      }
    } catch (error) {
      console.error("Failed to sync provider attributes before checkout:", error);
      submitter?.removeAttribute?.("aria-busy");
    }
  }

  showProviderRequiredMessage(sourceElement = null) {
    this.updateCartCheckoutState();

    const sourceFooter = sourceElement?.closest?.("[data-provider-required-footer]");
    const footerProvider = sourceFooter?.querySelector?.(
      "[data-cart-drawer-provider]",
    );
    const cartPageProvider = document.querySelector(
      ".cart__footer-wrapper [data-cart-drawer-provider]",
    );
    const providerSection = [
      footerProvider,
      document.body?.classList.contains("template-cart") ? cartPageProvider : null,
      document.querySelector("#CartDrawer [data-cart-drawer-provider]"),
      cartPageProvider,
      document.querySelector(".providers-map-section"),
      document.querySelector(".provider-selected-info-section"),
    ].find((element) => {
      if (!element) return false;
      return element.getClientRects().length > 0;
    });

    if (providerSection) {
      providerSection.scrollIntoView({ behavior: "smooth", block: "center" });
      providerSection.classList.remove("is-attention");
      void providerSection.offsetWidth;
      providerSection.classList.add("is-attention");
    }
  }

  updateCartCheckoutState() {
    const isRequired = this.isProviderRequiredForCheckout();

    document.querySelectorAll("[data-provider-required-footer]").forEach((footer) => {
      footer.classList.toggle("wt-cart__footer__body--provider-required", isRequired);
    });

    document
      .querySelectorAll("[data-provider-required-checkout]")
      .forEach((button) => {
        if (isRequired) {
          button.setAttribute("aria-disabled", "true");
        } else {
          button.removeAttribute("aria-disabled");
        }
      });

    document
      .querySelectorAll("[data-provider-required-dynamic-checkout]")
      .forEach((container) => {
        container.setAttribute("aria-disabled", isRequired ? "true" : "false");
        container.hidden = isRequired;
      });

    document
      .querySelectorAll("[data-cart-drawer-provider]")
      .forEach((providerSelector) => {
        providerSelector.classList.toggle("is-required", isRequired);
      });
  }
}

/**
 * @type {Window & {DTCCookieManager: DTCCookieManager}}
 */
// @ts-ignore
const globalWindow = window;

// Create global instance
globalWindow.DTCCookieManager = new DTCCookieManager();

// Auto-initialize from URL params on page load
document.addEventListener("DOMContentLoaded", function () {
  globalWindow.DTCCookieManager.initializeFromUrlParams();
});

// Also initialize immediately if DOM is already loaded
if (document.readyState === "loading") {
  // DOM is still loading, use DOMContentLoaded
} else {
  // DOM is already loaded
  globalWindow.DTCCookieManager.initializeFromUrlParams();
}

// Export for module use if needed
if (typeof module !== "undefined" && module.exports) {
  module.exports = DTCCookieManager;
}
